import React, { useEffect, useState, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';

import {
  useChangePasswordMutation,
  useGetAdminProfileQuery,
  useUpdateAdminProfileMutation,
  useUploadProfileImageMutation,
} from '@/store/api/profileApi';

import { useUploadFileMutation } from '@/store/api/uploadApi';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import toast from 'react-hot-toast';
import { Camera, Eye, EyeOff, Loader2, Mail, User } from 'lucide-react';

const profileSchema = z.object({
  firstname: z.string().min(1, 'First name is required').max(25, 'max 25 character allow'),
  lastname: z.string().min(1, 'Last name is required').max(25, 'max 25 character allow'),
  email: z.string().min(1, 'Email is required').email('Invalid email address'),
});

type ProfileFormValues = z.infer<typeof profileSchema>;

const passwordSchema = z
  .object({
    oldPassword: z.string().min(1, 'Current password is required'),

    newPassword: z
      .string()
      .regex(/^\S+$/, 'Password must not contain spaces')
      .min(8, 'Password must be at least 8 characters')
      .regex(/[A-Z]/, 'Must contain at least one uppercase letter')
      .regex(/[a-z]/, 'Must contain at least one lowercase letter')
      .regex(/[0-9]/, 'Must contain at least one number')
      .regex(/[!@#$%^&*(),.?":{}|<>]/, 'Must contain at least one special character'),
    confirmPassword: z.string().min(1, 'Confirm password is required'),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  });

type PasswordFormValues = z.infer<typeof passwordSchema>;

export default function AdminProfilePage() {
  const { data, refetch } = useGetAdminProfileQuery();

  const [updateProfile, { isLoading }] = useUpdateAdminProfileMutation();
  const [updateImage] = useUploadProfileImageMutation();
  const [uploadFile] = useUploadFileMutation();
  const [changePassword, { isLoading: passwordLoading }] = useChangePasswordMutation();
  const [imageLoading, setImageLoading] = useState(false);
  const [previewImage, setPreviewImage] = useState<string | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const [showPassword, setShowPassword] = useState(false);
  const [showPassword1, setShowPassword1] = useState(false);
  const [showPassword2, setShowPassword2] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ProfileFormValues>({
    resolver: zodResolver(profileSchema),
    defaultValues: {
      firstname: '',
      lastname: '',
      email: '',
    },
  });

  const {
    register: registerPassword,
    handleSubmit: handlePasswordSubmit,
    reset: resetPassword,
    formState: { errors: passwordErrors },
  } = useForm<PasswordFormValues>({
    resolver: zodResolver(passwordSchema),
    mode: 'onChange',
  });

  useEffect(() => {
    if (data) {
      reset({
        firstname: data.firstname || '',
        lastname: data.lastname || '',
        email: data.email || '',
      });
      if (!previewImage && data.profileImage) {
        setPreviewImage(data.profileImage);
      }
    }
  }, [data, reset]);

  const onSubmit = async (values: ProfileFormValues) => {
    try {
      await updateProfile(values).unwrap();
      toast.success('Profile updated successfully');
    } catch (err: any) {
      toast.error(err?.data?.message || 'Update failed');
    }
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const localPreview = URL.createObjectURL(file);
    setPreviewImage(localPreview);

    try {
      setImageLoading(true);

      const uploadRes = await uploadFile({ file, kind: 'image' }).unwrap();
      const imageUrl = uploadRes?.meta?.imageUrl;

      if (!imageUrl) throw new Error('Upload failed');

      await updateImage({ profileImage: imageUrl }).unwrap();

      setPreviewImage(imageUrl);

      toast.success('Profile image updated');
    } catch (err: any) {
      console.log('ERROR:', err);

      setPreviewImage(data?.profileImage || null);
      toast.error(err?.data?.message || 'Image upload failed');
    } finally {
      setImageLoading(false);
      URL.revokeObjectURL(localPreview);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const displayImage = previewImage || data?.profileImage;

  const onPasswordSubmit = async (values: PasswordFormValues) => {
    try {
      await changePassword({
        oldPassword: values.oldPassword,
        newPassword: values.newPassword,
      }).unwrap();

      toast.success('Password changed successfully');
      resetPassword();
    } catch (err: any) {
      toast.error(err?.data?.message || 'Password change failed');
    }
  };

  return (
    <div className="w-full p-4 md:p-6 flex flex-col xl:flex-row gap-5">
      <Card className="w-full xl:flex-1 shadow-md rounded-2xl overflow-hidden border-0">
        <div
          className="h-32 w-full  bg-[#4B0082]"
          style={{
            backgroundSize: '12px 12px',
          }}
        >
          {' '}
          <CardHeader>
            <CardTitle className="text-xl font-semibold text-white">Admin Profile</CardTitle>
          </CardHeader>
        </div>

        <CardContent className=" p-4 sm:px-8 pb-8 pt-0">
          <div className="flex flex-col sm:flex-row items-center sm:items-start gap-5 -mt-12 mb-8">
            <div className="relative group shrink-0">
              <div className="w-28 h-28 rounded-[2rem] overflow-hidden border-4 border-white bg-slate-50 flex items-center justify-center shadow-sm">
                {displayImage ? (
                  <img src={displayImage} alt="Profile" className="w-full h-full object-cover" />
                ) : (
                  <User className="w-12 h-12 text-slate-300" />
                )}
              </div>

              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={imageLoading}
                className="absolute -bottom-1 -right-1 w-9 h-9 rounded-xl bg-[#4B0082] shadow-md border border-slate-100 flex items-center justify-center text-slate-500 hover:text-[#635BFF] transition-colors disabled:cursor-not-allowed"
              >
                {imageLoading ? (
                  <Loader2 className="w-5 h-5 text-white animate-spin" />
                ) : (
                  <Camera className="w-4 h-4 text-white " />
                )}
              </button>
            </div>

            {/*<p className="text-xs text-muted-foreground">
              {imageLoading ? 'Uploading…' : 'Click avatar to change photo'}
            </p>*/}
            <div className="pt-2 sm:pt-14 text-center sm:text-left">
              <h2 className="text-xl font-bold text-darkPurple">
                {data?.firstname + ' ' + data?.lastname}
              </h2>
              <div className="flex items-center text-sm text-[#62556B]  mt-1 font-medium">
                <Mail className="w-4 h-4 mr-1.5" />
                {data?.email}
              </div>
            </div>

            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleImageUpload}
              disabled={imageLoading}
              className="hidden"
            />
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div className="space-y-1">
              <label className="text-sm  text-[#62556B] font-medium  ">First Name</label>
              <Input
                placeholder="First Name"
                {...register('firstname')}
                className={errors.firstname ? 'border-red-500 focus-visible:ring-red-500' : ''}
              />
              {errors.firstname && (
                <p className="text-xs text-red-500">{errors.firstname.message}</p>
              )}
            </div>

            <div className="space-y-1">
              <label className="text-sm  text-[#62556B] font-medium  ">Last Name</label>
              <Input
                placeholder="Last Name"
                {...register('lastname')}
                className={errors.lastname ? 'border-red-500 focus-visible:ring-red-500' : ''}
              />
              {errors.lastname && <p className="text-xs text-red-500">{errors.lastname.message}</p>}
            </div>
            <div className="space-y-1">
              <label className="text-sm  text-[#62556B] font-medium  ">Email</label>
              <Input placeholder="Email" {...register('email')} className="" />
              {errors.email && <p className="text-xs text-red-500">{errors.email.message}</p>}
            </div>

            <Button
              type="submit"
              className="w-full bg-darkPurple hover:bg-darkPurple/90 text-white"
              disabled={isLoading}
            >
              {isLoading ? (
                <span className="flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Updating…
                </span>
              ) : (
                'Update Profile'
              )}
            </Button>
          </form>
        </CardContent>
      </Card>

      <Card className="w-full xl:w-[380px] shadow-md rounded-2xl">
        <CardHeader>
          <CardTitle className="text-lg font-semibold text-darkPurple">Change Password</CardTitle>
        </CardHeader>

        <CardContent>
          <form onSubmit={handlePasswordSubmit(onPasswordSubmit)} className="space-y-4">
            <div className="space-y-1">
              <label className="text-sm  text-[#62556B]  font-medium">Current Password</label>
              <div className="relative">
                <Input
                  type={showPassword ? 'text' : 'password'}
                  {...registerPassword('oldPassword')}
                />

                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4 text-black" />
                  ) : (
                    <Eye className="h-4 w-4 text-black" />
                  )}
                </Button>
              </div>

              {passwordErrors.oldPassword && (
                <p className="text-xs text-red-500">{passwordErrors.oldPassword.message}</p>
              )}
            </div>
            <div className="space-y-1">
              <label className="text-sm  text-[#62556B]  font-medium">New Password</label>
              <div className="relative">
                <Input
                  type={showPassword1 ? 'text' : 'password'}
                  {...registerPassword('newPassword')}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                  onClick={() => setShowPassword1(!showPassword1)}
                >
                  {showPassword1 ? (
                    <EyeOff className="h-4 w-4 text-black" />
                  ) : (
                    <Eye className="h-4 w-4 text-black" />
                  )}
                </Button>
              </div>
              {passwordErrors.newPassword && (
                <p className="text-xs text-red-500">{passwordErrors.newPassword.message}</p>
              )}
            </div>

            <div className="space-y-1">
              <label className="text-sm  text-[#62556B] font-medium">Confirm Password</label>

              <div className="relative">
                <Input
                  type={showPassword2 ? 'text' : 'password'}
                  {...registerPassword('confirmPassword')}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                  onClick={() => setShowPassword2(!showPassword2)}
                >
                  {showPassword2 ? (
                    <EyeOff className="h-4 w-4 text-black" />
                  ) : (
                    <Eye className="h-4 w-4 text-black" />
                  )}
                </Button>
              </div>
              {passwordErrors.confirmPassword && (
                <p className="text-xs text-red-500">{passwordErrors.confirmPassword.message}</p>
              )}
            </div>

            <Button
              type="submit"
              className="w-full bg-darkPurple text-white "
              disabled={passwordLoading}
            >
              {passwordLoading ? (
                <span className="flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Changing…
                </span>
              ) : (
                'Change Password'
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
